import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons'
import { Horizontal, NothingMessage, Texto, Vertical, useLocalStorage } from '@gravitate-js/excalibrr'
import { Alert, Card, Modal, Splitter } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { useUserSettings } from '@/hooks/settings'
import { GraviButton, MaterialIcon } from '@gravitate-js/excalibrr'
import type { GridApi } from 'ag-grid-community'
import type {
  CreditRebillResponse,
  Invoice,
  InvoiceDetail,
  InvoiceTransaction,
  TransactionRow,
} from '../../../../api/useFreight/types'
import { useInvoices } from '../../../../api/useFreight/useInvoices'
import { OrderFreightDrawer } from '../../OrderFreightDetails/components/OrderFreightDrawer'
import { DetailsLineItemsGrid } from './components/DetailsLineItemsGrid'
import { InvoiceDetailPane } from './components/InvoiceDetailPane'
import { InvoiceNavigationHeader } from './components/InvoiceNavigationHeader'
import { InvoiceSummaryCards } from './components/InvoiceSummaryCards'
import { MultipleInvoiceTab } from './components/MultipleInvoiceTab'
import { OrderLog } from './components/OrderLog'
import { RebillFooter } from './components/RebillFooter'
import { SingleOrderDisplay } from './components/SingleOrderDisplay'
import { useFilteredLineItems, useRebillState } from './hooks'

// Initialize dayjs relative time plugin
dayjs.extend(relativeTime)

/**
 * Page component for detailed invoice view
 * Shows invoice details, line items, and provides rebill functionality
 */
const settingsKey = 'invoice-detail-page-settings'
export function InvoiceDetailPage() {
  const { id } = useParams()
  const location = useLocation()
  const { scopes } = useSecurityContext()
  const { getSetting, updateSetting } = useUserSettings()

  // Fetch invoice data
  const { useInvoiceById } = useInvoices()
  const { data: invoiceGroupDetails, isLoading } = useInvoiceById(id)

  // State for selected invoice and UI controls
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [invoiceTabs, setInvoiceTabs] = useState<Invoice[]>([])
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [drawerOrderId, setDrawerOrderId] = useState<string | null>(null)
  const { value: pageSettings, setValue: setPageSettings } = useLocalStorage<{ mapPanelHeight: string | number }>(
    settingsKey,
    { mapPanelHeight: '50%' }
  )

  // Check if this invoice has only a single order
  const isSingleOrder = selectedInvoice?.transactions?.length === 1

  // References for grids
  const freightLinesGridRef = useRef<GridApi<InvoiceDetail>>(null)
  const orderLogGridRef = useRef<GridApi<InvoiceTransaction>>(null)

  // Use the refactored rebill state hook
  const {
    isRecalcModalVisible,
    rebillInvoiceChain,
    isTriggeringCR,
    isRebillMode,
    hasProcessedRebillParam,
    setHasProcessedRebillParam,
    isConfirmRebillModalVisible,
    setIsConfirmRebillModalVisible,
    rebillMetadata,
    triggerRebillMode,
    overrideRebillLineItem,
    addRebillLineItem,
    setIsRebillMode,
    isSavingRebill,
    handleSaveRebill,
  } = useRebillState({
    setSelectedInvoice,
    selectedInvoice,
    invoiceGroupDetails,
  })

  const reversedInvoiceGroupDetails = useMemo(() => {
    if (!invoiceGroupDetails) return
    return {
      ...invoiceGroupDetails,
      invoices: [...invoiceGroupDetails.invoices].reverse(),
    }
  }, [invoiceGroupDetails])

  // Ensure rebillMetadata has the right type
  const typedRebillMetadata = rebillMetadata as unknown as CreditRebillResponse

  // Use the filtered line items hook
  const { filteredFreightLines, setSelectedOrder, selectedOrder, setSelectedAccessorialType, selectedAccessorialType } =
    useFilteredLineItems({
      selectedInvoice,
    })

  useEffect(() => {
    getSetting(settingsKey).then((storedSettings) => {
      if (storedSettings) {
        setPageSettings(storedSettings)
      }
    })
  }, [])

  // Set up invoice tabs when invoiceGroupDetails changes
  useEffect(() => {
    // If loading or no invoice details, clear tabs and selection
    if (isLoading || !invoiceGroupDetails?.invoices) {
      setInvoiceTabs([])
      // Don't clear selectedInvoice if we are in the middle of a rebill process
      if (!isRebillMode) {
        setSelectedInvoice(null)
      }
      return
    }

    // Sort invoices from the current invoiceGroupDetails
    const sortedInvoices = [...invoiceGroupDetails.invoices].sort((a, b) => {
      return b.invoice_number?.localeCompare(a.invoice_number)
    })

    setInvoiceTabs(sortedInvoices)

    // If not in rebill mode, always reset the selected invoice to the first one
    // of the *newly loaded* invoice group when invoiceGroupDetails changes.
    if (!isRebillMode) {
      if (sortedInvoices.length > 0) {
        setSelectedInvoice(sortedInvoices[0])
      } else {
        // Handle case where the new group might have no invoices
        setSelectedInvoice(null)
      }
    }
    // If isRebillMode is true, we assume the rebill process manages selectedInvoice correctly.
  }, [invoiceGroupDetails, isLoading, isRebillMode])

  // Check for triggerRebill parameter in URL and show confirmation modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const shouldTriggerRebill = searchParams.get('triggerRebill') === 'true'

    if (
      shouldTriggerRebill &&
      invoiceGroupDetails &&
      !hasProcessedRebillParam &&
      !isRecalcModalVisible &&
      !isTriggeringCR &&
      !isConfirmRebillModalVisible
    ) {
      // Mark that we've processed this parameter
      setHasProcessedRebillParam(true)

      // Clear the URL parameter immediately to prevent re-triggering
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)

      // Show confirmation modal instead of immediately triggering
      setIsConfirmRebillModalVisible(true)
    }
  }, [
    invoiceGroupDetails,
    location.search,
    isRecalcModalVisible,
    isTriggeringCR,
    hasProcessedRebillParam,
    isConfirmRebillModalVisible,
    setHasProcessedRebillParam,
    setIsConfirmRebillModalVisible,
  ])

  const handleConfirmRebill = () => {
    setIsConfirmRebillModalVisible(false)
    triggerRebillMode()
  }

  const handleCancelRebill = () => {
    setIsConfirmRebillModalVisible(false)
    // No need to do anything else, just close the modal
  }

  const triggerDrawer = (orderId: string) => {
    setDrawerOrderId(orderId)
    setDrawerVisible(true)
  }

  // Find the index of the order in the transactions array
  const findOrderIndex = (orderId: string | null) => {
    return selectedInvoice?.transactions?.findIndex((order) => order.order_id === orderId) || 0
  }

  // Handle navigation between orders in the drawer
  const handleNavigate = (newIndex: number) => {
    if (selectedInvoice?.transactions?.[newIndex]) {
      setDrawerOrderId(selectedInvoice.transactions[newIndex].order_id)
    }
  }

  function handlePanelResize(sizes: number[]) {
    setPageSettings({ ...pageSettings, mapPanelHeight: sizes[0] })
    updateSetting(settingsKey, { ...pageSettings, mapPanelHeight: sizes[0] })
  }

  // Handle Loading state explicitly
  if (isLoading) {
    return (
      <Vertical bg='default'>
        <InvoiceNavigationHeader />
        <Vertical flex={1} horizontalCenter verticalCenter>
          <LoadingOutlined style={{ fontSize: '48px' }} />
        </Vertical>
      </Vertical>
    )
  }

  // Handle case where loading is finished but no invoice group was found
  if (!invoiceGroupDetails || !reversedInvoiceGroupDetails) {
    return (
      <Vertical verticalCenter horizontalCenter style={{ padding: '24px' }}>
        <NothingMessage title='No invoice details found.' message='Could not load data for this invoice ID.' />
        <Vertical gap={16}>
          <Link to='/freight/invoices'>
            <GraviButton icon={<ArrowLeftOutlined />} buttonText='Back to Invoices' />
          </Link>
        </Vertical>
      </Vertical>
    )
  }

  // Handle case where invoice group exists but has no invoices (or selectedInvoice hasn't been set yet)
  if (!selectedInvoice) {
    // This might indicate an empty invoice group after the useEffect runs
    return (
      <Vertical verticalCenter horizontalCenter style={{ padding: '24px' }}>
        <NothingMessage title='Empty Invoice Group' message='This invoice group does not contain any invoices.' />
        <Vertical gap={16}>
          <Link to='/freight/invoices'>
            <GraviButton icon={<ArrowLeftOutlined />} buttonText='Back to Invoices' />
          </Link>
        </Vertical>
      </Vertical>
    )
  }

  const isNotSentOrBlocked = !['sent', 'blocked'].includes(selectedInvoice.status)
  const showAddFLIRebill =
    !scopes?.is_carrier &&
    (!isRebillMode || selectedInvoice.type === 'rebill') &&
    (selectedInvoice.status === 'cr_approval' || isNotSentOrBlocked)
  const showAddFLI =
    (!scopes?.is_carrier && !isRebillMode && isNotSentOrBlocked) ||
    (isRebillMode && selectedInvoice.type === 'rebill' && isNotSentOrBlocked)
  const hideGridActions =
    scopes?.is_carrier ||
    selectedInvoice.status === 'sent' ||
    selectedInvoice.type === 'credit' ||
    (isRebillMode && selectedInvoice.type !== 'rebill')

  return (
    <Vertical>
      <InvoiceNavigationHeader isRebillMode={isRebillMode} setSelectedOrder={setSelectedOrder} />

      <Modal
        title='Confirm Credit/Rebill'
        open={isConfirmRebillModalVisible}
        onOk={handleConfirmRebill}
        onCancel={handleCancelRebill}
        okText='Proceed'
        cancelText='Cancel'
        centered
      >
        <Vertical gap={16}>
          <Texto>Are you sure you want to initiate a credit/rebill process for this invoice?</Texto>
        </Vertical>
      </Modal>

      {/* Existing Recalculation Modal */}
      <Modal title='Recalculating Invoice' open={isRecalcModalVisible} footer={null} closable={false} centered>
        <Vertical horizontalCenter verticalCenter gap={16}>
          <LoadingOutlined style={{ fontSize: '24px' }} spin />
          <div>Please wait while we recalculate the invoice...</div>
        </Vertical>
      </Modal>

      <Horizontal flex={1}>
        {!isRebillMode && (
          <Vertical bg='elevated' flex={1}>
            <InvoiceDetailPane
              invoiceGroup={reversedInvoiceGroupDetails}
              triggerRebillMode={triggerRebillMode}
              isTriggeringCR={isTriggeringCR}
            />
          </Vertical>
        )}

        <Vertical style={{ flex: 4 }}>
          <Card
            style={{
              height: '100%',
              borderRadius: '0 0 6px 6px',
            }}
            styles={{
              body: {
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                height: '100%',
                padding: '0 8px 0 8px',
              },
            }}
          >
            <MultipleInvoiceTab
              selectedInvoice={selectedInvoice}
              setSelectedInvoice={setSelectedInvoice}
              invoiceTabs={isRebillMode ? rebillInvoiceChain : invoiceTabs}
            />

            {isRebillMode && (
              <Alert
                showIcon
                icon={<MaterialIcon className='pt-2' icon='currency_exchange' />}
                message={
                  <Texto category='p2' weight='bold'>
                    Credit / Rebill in progress.
                  </Texto>
                }
                description='You have triggered a credit / rebill. Please review the updated transactions and adjust as needed. The invoice will not be finalized until the credit / rebill is approved.'
                type='warning'
              />
            )}

            <InvoiceSummaryCards
              creditRebillMetadata={typedRebillMetadata}
              invoice={selectedInvoice}
              selectedAccessorialType={selectedAccessorialType as 'Accessorial' | 'Base Freight' | 'Surcharge'}
              setSelectedAccessorialType={setSelectedAccessorialType}
            />

            {/* Ensure filtered lines are ready before rendering grids */}
            {filteredFreightLines ? (
              isSingleOrder ? (
                <Splitter lazy layout='vertical' onResize={handlePanelResize}>
                  {!isRebillMode && (
                    <Splitter.Panel min={'5%'} size={pageSettings?.mapPanelHeight || '50%'}>
                      <SingleOrderDisplay order={selectedInvoice.transactions[0]} />
                    </Splitter.Panel>
                  )}
                  <Splitter.Panel min={'7%'}>
                    <DetailsLineItemsGrid
                      filteredFreightLines={filteredFreightLines}
                      freightLinesGridRef={freightLinesGridRef as MutableRefObject<GridApi<InvoiceDetail>>}
                      showAddAccessorial={showAddFLI}
                      hideStatus={invoiceGroupDetails.status === 'cr_approval'}
                      hideActions={hideGridActions}
                      rebillUpdateActions={{
                        overrideRebillLineItem,
                        //@ts-ignore
                        addRebillLineItem,
                      }}
                      isRebillMode={isRebillMode}
                      singleOrder={selectedInvoice.transactions[0]}
                      invoiceGroupDetails={invoiceGroupDetails}
                      filters={selectedAccessorialType ? { type: selectedAccessorialType } : undefined}
                      onFilterRemoved={() => setSelectedAccessorialType(null)}
                    />
                  </Splitter.Panel>
                </Splitter>
              ) : (
                <Horizontal flex={1} style={{ overflow: 'hidden' }}>
                  <OrderLog
                    invoiceGroupDetails={invoiceGroupDetails}
                    triggerDrawer={triggerDrawer}
                    selectedInvoice={selectedInvoice}
                    setSelectedOrder={setSelectedOrder}
                    orderLogGridRef={orderLogGridRef as MutableRefObject<GridApi<InvoiceTransaction>>}
                    selectedOrder={selectedOrder}
                    isRebillMode={isRebillMode}
                    rebillUpdateActions={{
                      addRebillLineItem,
                    }}
                  />
                  <DetailsLineItemsGrid
                    rebillUpdateActions={{
                      overrideRebillLineItem,
                    }}
                    hideStatus={invoiceGroupDetails.status === 'cr_approval'}
                    filteredFreightLines={filteredFreightLines}
                    selectedOrder={selectedOrder}
                    freightLinesGridRef={freightLinesGridRef as MutableRefObject<GridApi<InvoiceDetail>>}
                    showAddAccessorial={showAddFLIRebill}
                    hideActions={
                      selectedInvoice.status === 'sent' ||
                      selectedInvoice.type === 'credit' ||
                      (isRebillMode && selectedInvoice.type !== 'rebill')
                    }
                    isRebillMode={isRebillMode}
                    singleOrder={selectedOrder || ({} as InvoiceTransaction)}
                    invoiceGroupDetails={invoiceGroupDetails}
                    filters={selectedAccessorialType ? { type: selectedAccessorialType } : undefined}
                    onFilterRemoved={() => setSelectedAccessorialType(null)}
                  />
                </Horizontal>
              )
            ) : (
              // Optionally render a loader or nothing if filteredFreightLines aren't ready yet
              <Vertical flex={1} horizontalCenter verticalCenter>
                {/* You could put a <LoadingOutlined /> here if preferred */}
              </Vertical>
            )}
          </Card>
        </Vertical>
      </Horizontal>

      {isRebillMode && (
        <RebillFooter
          isSavingRebill={isSavingRebill}
          handleSaveRebill={handleSaveRebill}
          rebillInvoice={invoiceTabs.find((invoice) => invoice.type === 'rebill') || invoiceTabs[0]}
          setIsRebillMode={setIsRebillMode}
          rebillMetadata={typedRebillMetadata}
        />
      )}

      {drawerVisible && (
        <OrderFreightDrawer
          costRevenue={invoiceGroupDetails.book_type}
          open={drawerVisible}
          order={
            selectedInvoice.transactions.find((order) => order.order_id === drawerOrderId) as
              | InvoiceTransaction
              | TransactionRow
          }
          onClose={() => {
            setDrawerVisible(false)
          }}
          allOrders={selectedInvoice.transactions}
          currentIndex={findOrderIndex(drawerOrderId)}
          onNavigate={handleNavigate}
        />
      )}
    </Vertical>
  )
}
